import {
  PublicListingApplicationDocumentClassifier,
  PublicListingApplicationType,
  type PublicListingApplicationContactInput,
  type PublicListingApplicationDocumentInput,
  type PublicListingApplicationInput,
} from "@/graphql/generated/public";
import { importFieldError, type ImportFieldError } from "@/lib/import-field-validation";
import type { MailLanguageChoice } from "./MailLanguageField";

// Die öffentliche Bewerbung ohne Konto (@external-adr GENESIS-0072-applying-without-an-account-confirms-email-before-a-person-exists,
// @external-adr GENESIS-0083-a-company-applies-without-an-account-through-its-proven-contact). Anders als mit Konto ist
// hier kein Profil bekannt: alle Kontaktfelder sind frei, Vorname und E-Mail Pflicht. Geprüft wird mit denselben Regeln
// wie im Profil-Editor; genesis prüft dieselben Grenzen noch einmal und antwortet in jedem Fall gleich.

export type PublicContactField =
  | "givenName"
  | "familyName"
  | "email"
  | "phone"
  | "website"
  | "street"
  | "houseNumber"
  | "zipCode"
  | "city";

export type PublicContactValues = Record<PublicContactField, string>;

export const EMPTY_PUBLIC_CONTACT: PublicContactValues = {
  givenName: "",
  familyName: "",
  email: "",
  phone: "",
  website: "",
  street: "",
  houseNumber: "",
  zipCode: "",
  city: "",
};

/** Die Feldfehler: Pflicht für Vorname und E-Mail, sonst die Formatregeln des Profil-Editors. */
export function publicContactErrors(
  values: PublicContactValues,
): Partial<Record<PublicContactField, ImportFieldError>> {
  const errors: Partial<Record<PublicContactField, ImportFieldError>> = {};
  for (const field of Object.keys(EMPTY_PUBLIC_CONTACT) as PublicContactField[]) {
    const error = importFieldError(field, values[field]);
    if (error) errors[field] = error;
  }
  return errors;
}

/** Pflichtfelder, die noch leer sind. */
export function missingPublicFields(values: PublicContactValues): PublicContactField[] {
  return (["givenName", "email"] as const).filter((field) => !values[field].trim());
}

/**
 * Die Fassung des Einwilligungstexts, die das Formular zeigt (`share.public.apply.consent`). Ändert sich der Text,
 * muss sich diese Fassung ändern: genesis hält sie an der Bewerbung fest, und nur so weiß man später, welchem Text
 * zugestimmt wurde.
 */
export const PUBLIC_APPLICATION_CONSENT_VERSION = "spark-public-application-consent-2026-10-31";

export interface PublicApplicationDraft {
  listingId: string;
  company: boolean;
  companyName: string;
  contact: PublicContactValues;
  language: MailLanguageChoice;
  documents: { fileName: string; content: string; classifier?: "CURRICULUM_VITAE" }[];
}

/** Was an `submitListingApplication` geht: nur ausgefüllte Felder, Typ ausdrücklich, Sprache nur bei Wahl. */
export function publicApplicationInput(draft: PublicApplicationDraft): PublicListingApplicationInput {
  const contact: PublicListingApplicationContactInput = {
    givenName: draft.contact.givenName.trim(),
    email: draft.contact.email.trim(),
  };
  for (const field of Object.keys(EMPTY_PUBLIC_CONTACT) as PublicContactField[]) {
    const value = draft.contact[field].trim();
    if (value) contact[field] = value;
  }
  const documents: PublicListingApplicationDocumentInput[] = draft.documents.map((document) => ({
    fileName: document.fileName,
    content: document.content,
    ...(document.classifier ? { classifier: PublicListingApplicationDocumentClassifier.CurriculumVitae } : {}),
  }));
  return {
    listingId: draft.listingId,
    contact,
    consent: { accepted: true, version: PUBLIC_APPLICATION_CONSENT_VERSION },
    applicationType: draft.company ? PublicListingApplicationType.Company : PublicListingApplicationType.Person,
    ...(draft.company ? { companyName: draft.companyName.trim() } : {}),
    ...(documents.length ? { documents } : {}),
    // Nur eine ausdrückliche Wahl; ohne sie schreibt genesis in der Standardsprache der Plattform.
    ...(draft.language ? { locale: draft.language === "EN" ? "en" : "de" } : {}),
  };
}

/** Wie eine Einreichung ausging, so weit der Browser es wissen kann. */
export type PublicSubmission =
  /** Der Server hat geantwortet (immer neutral): vielleicht kommt eine Mail, nichts darüber hinaus. */
  | "answered"
  /** Gerade zu viele Einreichungen: nichts wurde gelesen, ein neuer Versuch später ist gefahrlos. */
  | "busy"
  /** Keine oder keine lesbare Antwort: ob sie ankam, ist offen. Nie automatisch wiederholen. */
  | "unknown";

/** Der Marker, mit dem BFF und genesis eine Ablehnung vor jeder Verarbeitung kennzeichnen. */
export const ADMISSION_HEADER = "public-application-admission";

/**
 * Ordnet die Antwort der öffentlichen Einreichung ein. "busy" nur mit Marker: eine 503 ohne ihn kann auch ein Timeout
 * sein, nachdem genesis die Einreichung schon genommen hat.
 */
export function classifySubmission(status: number | null, headers?: Headers | null): PublicSubmission {
  if (status === 200) return "answered";
  if ((status === 429 || status === 503) && headers?.get(ADMISSION_HEADER) === "refused") return "busy";
  return "unknown";
}
